import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Add, ArrowDropDown, CloudUpload, Delete, Edit, Image as ImageIcon, Science } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { uk } from 'date-fns/locale';

import { PageIndices } from '../../common/i-doc/i-doc.registry.ts';
import { DashboardLayout } from '@components/DashboardLayout.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageHint } from '@components/common/PageHint.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';
import { toastService } from '@services/toast.service.ts';
import { newsService } from '@services/news.service.ts';
import { fileService } from '@services/file.service.ts';
import { NewsI18n, NewsItem, CreateNewsDto, NewsTargetAudience, NewsAudienceOption } from '@/types/news.ts';
import { getImageUrl } from '@utils/image-url.util.ts';
import { TranslationStatus } from '@components/common/TranslationStatus.tsx';
import { LocalizedInput } from '@components/common/LocalizedInput.tsx';
import { Locale } from '@services/workout.service.ts';

const LOCALES: Locale[] = ['uk', 'en', 'ru', 'cs', 'es', 'pl', 'be'];

interface NewsFormData {
  titleI18n: Partial<Record<Locale, string>>;
  contentI18n: Partial<Record<Locale, string>>;
  isPublished: boolean;
  publishedAt: Date | null;
  coverImageId?: number;
  targetAudience: NewsTargetAudience;
}

const initialFormData: NewsFormData = {
  titleI18n: {},
  contentI18n: {},
  isPublished: false,
  publishedAt: null,
  targetAudience: 'all',
};

export const AdminNewsPage: React.FC = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [audienceOptions, setAudienceOptions] = useState<NewsAudienceOption[]>([]);
  const [audienceFilter, setAudienceFilter] = useState<string>('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingNews, setEditingNews] = useState<NewsItem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [newsToDelete, setNewsToDelete] = useState<NewsItem | null>(null);
  const [testMenuAnchor, setTestMenuAnchor] = useState<null | HTMLElement>(null);

  const [formData, setFormData] = useState<NewsFormData>(initialFormData);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // FIX: заголовок і текст новини — два окремих LocalizedInput, кожен раніше
  // мав власну незалежну вкладку мови (внутрішній useState) — перемикання
  // мови для заголовка ніяк не відображалось на вкладці тексту. Один спільний
  // activeLocaleTab на весь діалог — перемикають синхронно.
  const [activeLocaleTab, setActiveLocaleTab] = useState(0);

  const fetchNews = async () => {
    setLoading(true);
    try {
      const response = await newsService.getAdminNews(page, limit, audienceFilter || undefined);
      setNews(response.data);
      setTotal(response.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити новини');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    newsService.getAdminNewsOptions()
      .then((opts) => setAudienceOptions(opts.audience))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchNews();
  }, [page, limit, audienceFilter]);

  const handleOpenDialog = (newsItem?: NewsItem) => {
    if (newsItem) {
      setEditingNews(newsItem);
      setFormData({
        titleI18n: newsItem.titleI18n,
        contentI18n: newsItem.contentI18n,
        isPublished: newsItem.isPublished,
        publishedAt: newsItem.publishedAt ? new Date(newsItem.publishedAt) : null,
        coverImageId: newsItem.coverImageId,
        targetAudience: newsItem.targetAudience ?? 'all',
      });
      setPreviewUrl(newsItem.coverImageUrl ? getImageUrl(newsItem.coverImageUrl) : null);
    } else {
      setEditingNews(null);
      setFormData(initialFormData);
      setPreviewUrl(null);
    }
    setSelectedFile(null);
    setActiveLocaleTab(0);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingNews(null);
    setSelectedFile(null);
    setPreviewUrl(null);
    setFormData(initialFormData);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleInputChange = (field: keyof NewsFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.titleI18n.uk || !formData.contentI18n.uk) {
      toastService.error("Заповніть обов'язкові поля (Українська версія)");
      return;
    }
    setSubmitting(true);
    try {
      let coverImageId = formData.coverImageId;
      if (selectedFile) {
        const uploadedFile = await fileService.uploadFile(selectedFile, 'News cover image');
        coverImageId = uploadedFile.id;
      }

      const newsData: CreateNewsDto = {
        titleI18n: formData.titleI18n as NewsI18n,
        contentI18n: formData.contentI18n as NewsI18n,
        isPublished: formData.isPublished,
        publishedAt: formData.publishedAt ? formData.publishedAt.toISOString() : undefined,
        coverImageId,
        targetAudience: formData.targetAudience,
      };

      if (editingNews) {
        await newsService.updateNews(editingNews.id, newsData);
        toastService.success('Новину оновлено');
      } else {
        await newsService.createNews(newsData);
        toastService.success('Новину створено');
      }

      handleCloseDialog();
      fetchNews();
    } catch {
      toastService.error('Помилка при збереженні новини');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateTestNews = async (audience: 'clients' | 'experts' | 'all') => {
    setTestMenuAnchor(null);
    try {
      setLoading(true);
      await newsService.createTestNews(audience);
      toastService.success('Тестову новину створено');
      fetchNews();
    } catch {
      toastService.error('Не вдалося створити тестову новину');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!newsToDelete) return;
    try {
      await newsService.deleteNews(newsToDelete.id);
      toastService.success('Новину видалено');
      fetchNews();
    } catch {
      toastService.error('Не вдалося видалити новину');
    } finally {
      setNewsToDelete(null);
    }
  };

  const getAvailableTranslations = (item: NewsItem): { [key in Locale]?: boolean } =>
    Object.fromEntries(LOCALES.map((lang) => [lang, !!item.titleI18n[lang]])) as { [key in Locale]?: boolean };

  return (
    <DashboardLayout>
      <Box data-i-doc={PageIndices.ADMIN_NEWS}>
        <PageHint iDoc={PageIndices.ADMIN_NEWS} />

        <PageCard>
          <PageToolbar
            filters={audienceOptions.length > 0 ? [{
              id: 'audience',
              type: 'custom',
              renderCustom: () => (
                <ToggleButtonGroup
                  value={audienceFilter}
                  exclusive
                  onChange={(_e, val) => { setAudienceFilter(val ?? ''); setPage(1); }}
                  size="small"
                >
                  {audienceOptions.map((opt) => (
                    <ToggleButton key={opt.value} value={opt.value}>{opt.label}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              ),
            }] : []}
            onReset={() => { setAudienceFilter(''); setPage(1); }}
            
            extraActions={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <ButtonGroup variant="outlined" color="secondary" disabled={loading}>
                  <Button startIcon={<Science />} onClick={() => handleCreateTestNews('all')}>
                    Тестова новина
                  </Button>
                  <Button size="small" onClick={(e) => setTestMenuAnchor(e.currentTarget)}>
                    <ArrowDropDown />
                  </Button>
                </ButtonGroup>
                <Menu
                  anchorEl={testMenuAnchor}
                  open={Boolean(testMenuAnchor)}
                  onClose={() => setTestMenuAnchor(null)}
                >
                  <MenuItem onClick={() => handleCreateTestNews('all')}>Всі</MenuItem>
                  <MenuItem onClick={() => handleCreateTestNews('clients')}>Клієнти</MenuItem>
                  <MenuItem onClick={() => handleCreateTestNews('experts')}>Експерти</MenuItem>
                </Menu>
                <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
                  Додати
                </Button>
              </Box>
            }
          />

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Зображення</TableCell>
                  <TableCell>Заголовок (UK)</TableCell>
                  <TableCell>Аудиторія</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Дата публікації</TableCell>
                  <TableCell>Дії</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {news.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.id}</TableCell>
                    <TableCell>
                      {item.coverImageUrl ? (
                        <Avatar
                          variant="rounded"
                          src={getImageUrl(item.coverImageUrl)}
                          alt={item.titleI18n.uk}
                          sx={{ width: 56, height: 56 }}
                        />
                      ) : (
                        <Avatar variant="rounded" sx={{ width: 56, height: 56 }}>
                          <ImageIcon />
                        </Avatar>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{item.titleI18n.uk}</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <TranslationStatus availableTranslations={getAvailableTranslations(item)} />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          item.targetAudience === 'clients' ? 'Клієнти' :
                          item.targetAudience === 'experts' ? 'Експерти' : 'Всі'
                        }
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.isPublished ? 'Опубліковано' : 'Чернетка'}
                        color={item.isPublished ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {item.publishedAt ? new Date(item.publishedAt).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleOpenDialog(item)} color="primary">
                        <Edit />
                      </IconButton>
                      <IconButton onClick={() => setNewsToDelete(item)} color="error">
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {news.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Новин немає
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <UniversalPagination
            page={page}
            count={Math.ceil(total / limit)}
            limit={limit}
            total={total}
            onPageChange={setPage}
            onLimitChange={(val) => { setLimit(val); setPage(1); }}
            storageKey="pagination:admin-news"
          />
        </PageCard>
      </Box>

      {/* Edit / Create Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>{editingNews ? 'Редагувати новину' : 'Створити новину'}</DialogTitle>
        <DialogContent>
          <form id="news-form" onSubmit={handleSubmit}>
            <Grid container spacing={3} sx={{ mt: 0 }}>
              <Grid item xs={12} md={8}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <LocalizedInput
                    label="Заголовок"
                    value={formData.titleI18n}
                    onChange={(v) => handleInputChange('titleI18n', v)}
                    activeTab={activeLocaleTab}
                    onTabChange={setActiveLocaleTab}
                  />
                  <LocalizedInput
                    label="Текст новини"
                    multiline
                    rows={12}
                    value={formData.contentI18n}
                    onChange={(v) => handleInputChange('contentI18n', v)}
                    activeTab={activeLocaleTab}
                    onTabChange={setActiveLocaleTab}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    Налаштування
                  </Typography>
                  <Stack spacing={3}>
                    <FormControl fullWidth>
                      <InputLabel>Аудиторія</InputLabel>
                      <Select
                        value={formData.targetAudience}
                        label="Аудиторія"
                        onChange={(e) => handleInputChange('targetAudience', e.target.value as NewsTargetAudience)}
                      >
                        <MenuItem value="all">Всі</MenuItem>
                        <MenuItem value="clients">Клієнти</MenuItem>
                        <MenuItem value="experts">Експерти</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isPublished}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setFormData(prev => ({
                              ...prev,
                              isPublished: isChecked,
                              publishedAt: isChecked && !prev.publishedAt ? new Date() : prev.publishedAt,
                            }));
                          }}
                        />
                      }
                      label="Опублікувати"
                    />

                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
                      <DateTimePicker
                        label="Дата публікації"
                        value={formData.publishedAt}
                        onChange={(newValue) => handleInputChange('publishedAt', newValue)}
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                    </LocalizationProvider>

                    <Divider />

                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Обкладинка
                      </Typography>
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="raised-button-file"
                        type="file"
                        onChange={handleFileChange}
                      />
                      <label htmlFor="raised-button-file">
                        <Button variant="outlined" component="span" fullWidth startIcon={<CloudUpload />}>
                          Завантажити
                        </Button>
                      </label>
                      {previewUrl && (
                        <Box mt={2} sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 1, display: 'flex', justifyContent: 'center' }}>
                          <img
                            src={previewUrl}
                            alt="Preview"
                            style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>Скасувати</Button>
          <Button type="submit" form="news-form" variant="contained" disabled={submitting}>
            {submitting ? 'Збереження...' : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={!!newsToDelete}
        onClose={() => setNewsToDelete(null)}
        onConfirm={confirmDelete}
        title="Підтвердити видалення"
        message={`Ви впевнені, що хочете видалити новину "${newsToDelete?.titleI18n.uk}"?`}
        confirmText="Видалити"
      />
    </DashboardLayout>
  );
};
